import {
  getFontCss,
  HoverableLinkDesigns,
  HoverableLinkSizes,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import Lnk, { AriaProps } from 'src/navigation/Lnk'
import { LocationState } from 'src/redux/createRootReducer'
import { css, CSSProp } from 'styled-components'

interface HoverableLinkPropsShared {
  className?: string
  id?: string
  onClick?: () => void
  size?: number | HoverableLinkSizes
  design?: HoverableLinkDesigns
  href?: string
  underline?: boolean
  hoverUnderline?: boolean
  regular?: boolean
  disabled?: boolean
  colorSvgOnHover?: boolean
  testId?: string
  loading?: boolean
}

export type HoverableLinkProps = HoverableLinkPropsShared &
  AriaProps & { to?: Location<LocationState> | string }

interface LinkCSSProps {
  design?: HoverableLinkDesigns
  size?: number | HoverableLinkSizes
  underline: boolean
  hoverUnderline?: boolean
  regular: boolean
  disabled: boolean
  loading?: boolean
  colorSvgOnHover?: boolean
}

export const getHoverableLinkCSS = (props: LinkCSSProps): CSSProp => {
  const fontSize =
    typeof props.size === 'number'
      ? props.size
      : props.size === HoverableLinkSizes.Small
        ? 12
        : props.size === HoverableLinkSizes.Large
          ? 16
          : 14
  const fontWeight = props.regular
    ? styleConstants.fontFamilies.regular
    : styleConstants.fontFamilies.medium
  return css`
    white-space: nowrap;
    cursor: pointer;
    transition: color ${styleConstants.transitionSpeed.fastest} ease-out;
    display: inline-flex;
    align-items: center;

    svg path,
    svg g {
      transition: fill ${styleConstants.transitionSpeed.fastest} ease-out;
    }

    ${getFontCss(fontSize, themeConstants.colors.cta, fontWeight)};
    &:hover {
      ${getFontCss(fontSize, themeConstants.colors.cta_dark, fontWeight)};

      ${!!props.colorSvgOnHover &&
      css`
        svg path,
        svg g {
          fill: ${themeConstants.colors.cta_dark};
        }
      `}
    }

    ${props.design === HoverableLinkDesigns.LightOnDark &&
    css`
      ${getFontCss(fontSize, styleConstants.colors.white, fontWeight)};
      &:hover {
        ${getFontCss(fontSize, styleConstants.colors.gray_medium, fontWeight)};
      }
    `}

    ${props.design === HoverableLinkDesigns.Primary &&
    css`
      ${getFontCss(fontSize, themeConstants.colors.font_primary, fontWeight)};
      &:hover {
        ${getFontCss(fontSize, themeConstants.colors.cta_dark, fontWeight)};
      }
    `}

    ${!!props.underline &&
    css`
      text-decoration: underline;
    `}

    ${(!!props.disabled || !!props.loading) &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

    ${!!props.hoverUnderline &&
    css`
      &:hover {
        text-decoration: underline;
      }
    `}
  `
}

const HoverableLink: React.FC<React.PropsWithChildren<HoverableLinkProps>> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const linkProps = {
    className: props.className,
    id: props.id,
    onClick: props.onClick,
  }

  const designProps = {
    design: props.design,
    size: props.size,
    underline: !!props.underline,
    hoverUnderline: !!props.hoverUnderline,
    regular: !!props.regular,
    disabled: !!props.disabled,
    colorSvgOnHover: !!props.colorSvgOnHover,
    loading: props.loading,
  }

  const ariaProps =
    props.ariaLabelSource !== undefined
      ? { ariaLabelSource: props.ariaLabelSource }
      : { ariaLabel: props.ariaLabel }

  if (props.to !== undefined) {
    return (
      <Lnk
        to={props.to}
        {...linkProps}
        {...ariaProps}
        testId={props.testId}
        css={getHoverableLinkCSS(designProps)}
      >
        {props.children}
      </Lnk>
    )
  } else {
    const content = (
      <>
        {!!props.loading && (
          <span
            css={css`
              margin-right: 4px;
            `}
          >
            <Processing />
          </span>
        )}
        {props.children}
      </>
    )

    return props.href !== undefined ? (
      <a
        data-testid={props.testId}
        className={props.className}
        id={props.id}
        css={getHoverableLinkCSS(designProps)}
        href={props.href}
        target={'_blank'}
        rel="noopener noreferrer"
        onClick={props.onClick}
        aria-label={
          props.ariaLabelSource !== undefined
            ? translate(props.ariaLabelSource)
            : props.ariaLabel
        }
      >
        {content}
      </a>
    ) : (
      <span
        data-testid={props.testId}
        className={props.className}
        id={props.id}
        aria-label={
          props.ariaLabelSource !== undefined
            ? translate(props.ariaLabelSource)
            : props.ariaLabel
        }
        css={getHoverableLinkCSS(designProps)}
        onClick={props.onClick}
        onKeyPress={props.onClick}
        role={'button'}
        tabIndex={0}
      >
        {content}
      </span>
    )
  }
}

export default HoverableLink
